import { Account } from '@shared/account/models';

const MS_PER_DAY = 86_400_000;
const DEFAULT_SESSION_SAMPLE_RATE = 55;
const FULL_SAMPLE_RATE = 100;

// Every session of an account this young is sent, and kept by the "New accounts" RUM
// retention filter (@usr.newAccount), so the weekly first-session review can follow it
// through to the end of the trial.
export const NEW_ACCOUNT_RUM_DAYS = 30;
// Replays are force-recorded only for the first session or two.
export const NEW_ACCOUNT_REPLAY_DAYS = 3;
export const NEW_ACCOUNT_CREATED_AT_STORAGE_KEY = 'tr-rum-new-account-created-at';
// Signups from Ukraine are left out of the review.
const EXCLUDED_COUNTRIES = new Set(['UA']);

export function isNewAccount(
  createdAt: number | null | undefined,
  country: string | null | undefined,
  now: number,
  days = NEW_ACCOUNT_RUM_DAYS,
): boolean {
  if (!createdAt || EXCLUDED_COUNTRIES.has(country ?? '')) {
    return false;
  }

  return now - createdAt <= days * MS_PER_DAY;
}

// A device clock behind the server's would make a fresh account look negative days old
export function accountAgeDays(createdAt: number, now: number): number {
  return Math.max(0, Math.floor((now - createdAt) / MS_PER_DAY));
}

/**
 * The SDK samples a session when it starts, before the user is known, and the rate is fixed
 * for the page's lifetime. Signed-out pages (signup, or a first login on this device) and
 * devices that remember a new account are sent in full.
 */
export function rumSessionSampleRate(
  signedIn: boolean,
  newAccountCreatedAt: number | null,
  now: number,
): number {
  return !signedIn || isNewAccount(newAccountCreatedAt, null, now)
    ? FULL_SAMPLE_RATE
    : DEFAULT_SESSION_SAMPLE_RATE;
}

export interface RumAccountAttributes {
  accountId: string;
  accountCreatedAt: string | null;
  accountAgeDays: number | null;
  industry: string | null;
  country: string | null;
  plan: string;
  planCode: string | null;
  newAccount: boolean;
}

export function rumAccountAttributes(
  account: Pick<Account, 'createdAt' | 'id' | 'industry'>,
  country: string | null | undefined,
  plan: string,
  planCode: string | null,
  now: number,
): RumAccountAttributes {
  const { createdAt } = account;

  return {
    accountId: account.id,
    accountCreatedAt: createdAt ? new Date(createdAt).toISOString() : null,
    accountAgeDays: createdAt ? accountAgeDays(createdAt, now) : null,
    industry: account.industry || null,
    country: country || null,
    plan,
    planCode,
    newAccount: isNewAccount(createdAt, country, now),
  };
}
